import { useCallback, useState } from 'react';
import {
    useGroupEditorRenameAction,
    useGroupEditorUngroupAction,
} from '@/components/group-editor/use-group-editor-actions';
import { useGroupEditorModalController } from '@/components/group-editor/use-group-editor-modal-controller';
import { useGroupEditorTableSelection } from '@/components/group-editor/use-group-editor-table-selection';

export type GroupEditorTargetBase = {
    id: string;
    name: string;
};

export type GroupEditorDataBase = {
    id: string;
    name: string;
};

export type UseGroupEditorAdapterArgs<
    TGroupEditorTarget extends GroupEditorTargetBase,
    TGroupEditorData extends GroupEditorDataBase,
    TSelectionItem,
    TRenameResponse extends { id: string; name: string },
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends { sourceGroupDeleted: boolean },
> = {
    accessToken: string;
    extractApiErrorMessage: (error: unknown) => string | null;
    loadGroupEditorDataRequest: (
        groupId: string,
        options: { authToken: string; signal?: AbortSignal },
    ) => Promise<TGroupEditorData>;
    refreshAfterMutation: () => Promise<void>;
    getItems: (groupEditorData: TGroupEditorData) => TSelectionItem[];
    getItemKey: (item: TSelectionItem) => string;
    renameGroupRequest: (
        groupId: string,
        payload: { name: string },
        options: { authToken: string },
    ) => Promise<{ data: TRenameResponse }>;
    mapGroupEditorDataAfterRename: (currentData: TGroupEditorData, response: TRenameResponse) => TGroupEditorData;
    mapOpenedGroupAfterRename: (currentGroup: TGroupEditorTarget, response: TRenameResponse) => TGroupEditorTarget;
    extractFromGroupRequest: (
        groupId: string,
        payload: TExtractPayload,
        options: { authToken: string },
    ) => Promise<{ data: TExtractResponse }>;
    buildExtractPayload: (selectedValues: string[]) => TExtractPayload;
    mapOpenedGroupAfterRefresh: (
        currentGroup: TGroupEditorTarget,
        refreshedGroupData: TGroupEditorData,
    ) => TGroupEditorTarget;
    ungroupMessages: {
        emptySelectionWarning: string;
        success: string;
        successDeleted: string;
        error: string;
    };
    initialRowsOnPage?: number;
};

export const useGroupEditorAdapter = <
    TGroupEditorTarget extends GroupEditorTargetBase,
    TGroupEditorData extends GroupEditorDataBase,
    TSelectionItem,
    TRenameResponse extends { id: string; name: string },
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends { sourceGroupDeleted: boolean },
>({
    accessToken,
    extractApiErrorMessage,
    loadGroupEditorDataRequest,
    refreshAfterMutation,
    getItems,
    getItemKey,
    renameGroupRequest,
    mapGroupEditorDataAfterRename,
    mapOpenedGroupAfterRename,
    extractFromGroupRequest,
    buildExtractPayload,
    mapOpenedGroupAfterRefresh,
    ungroupMessages,
    initialRowsOnPage,
}: UseGroupEditorAdapterArgs<
    TGroupEditorTarget,
    TGroupEditorData,
    TSelectionItem,
    TRenameResponse,
    TExtractPayload,
    TExtractResponse
>) => {
    const [pickedValues, setPickedValues] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [rowsOnPage, setRowsOnPage] = useState(initialRowsOnPage || 10);

    const resetSelectionState = useCallback(() => {
        setPickedValues([]);
        setPage(1);
    }, []);

    const loadGroupEditorData = useCallback(
        (groupId: string, signal?: AbortSignal) =>
            loadGroupEditorDataRequest(groupId, {
                authToken: accessToken,
                signal,
            }),
        [accessToken, loadGroupEditorDataRequest],
    );

    const {
        openedGroupEditor,
        setOpenedGroupEditor,
        groupEditorData,
        setGroupEditorData,
        groupEditorNameDraft,
        setGroupEditorNameDraft,
        isGroupEditorLoading,
        groupEditorError,
        isGroupEditorNameSaving,
        setIsGroupEditorNameSaving,
        isGroupEditorUngrouping,
        setIsGroupEditorUngrouping,
        normalizedGroupEditorNameDraft,
        hasGroupEditorNameChanged,
        onGroupEditorOpen,
        onGroupEditorClose,
        onGroupEditorModalVisibilityChange,
    } = useGroupEditorModalController<TGroupEditorTarget, TGroupEditorData>({
        loadGroupEditorData,
        resetSelectionState,
    });

    const {
        totalRows,
        pageCount,
        pagedItems,
        isHeaderChecked,
        checkboxData,
        onHeaderCheckboxChange,
        onCheckboxChange,
        onRowsOnPageUpdate,
    } = useGroupEditorTableSelection<TSelectionItem>({
        items: groupEditorData ? getItems(groupEditorData) : [],
        page,
        setPage,
        rowsOnPage,
        setRowsOnPage,
        pickedValues,
        setPickedValues,
        getItemKey,
    });

    const { renameGroupEditor, onGroupEditorRenameSubmit } = useGroupEditorRenameAction<
        TGroupEditorData,
        TGroupEditorTarget,
        TRenameResponse
    >({
        groupEditorData,
        groupEditorNameDraft,
        accessToken,
        extractApiErrorMessage,
        setGroupEditorData,
        setGroupEditorNameDraft,
        setOpenedGroupEditor,
        setIsGroupEditorNameSaving,
        renameGroupRequest,
        mapGroupEditorDataAfterRename,
        mapOpenedGroupAfterRename,
        refreshAfterRename: refreshAfterMutation,
    });

    const { onUngroupSelected } = useGroupEditorUngroupAction<
        TGroupEditorData,
        TGroupEditorTarget,
        string,
        TExtractPayload,
        TExtractResponse
    >({
        groupEditorData,
        selectedValues: pickedValues,
        accessToken,
        extractApiErrorMessage,
        setGroupEditorData,
        setGroupEditorNameDraft,
        setOpenedGroupEditor,
        setIsGroupEditorUngrouping,
        onGroupEditorClose,
        resetGroupEditorSelectionState: resetSelectionState,
        extractFromGroupRequest,
        buildExtractPayload,
        refreshAfterUngroup: refreshAfterMutation,
        loadGroupEditorData,
        mapOpenedGroupAfterRefresh,
        messages: ungroupMessages,
    });

    return {
        openedGroupEditor,
        groupEditorData,
        groupEditorNameDraft,
        setGroupEditorNameDraft,
        isGroupEditorLoading,
        groupEditorError,
        pickedValues,
        page,
        setPage,
        rowsOnPage,
        onRowsOnPageUpdate,
        isGroupEditorNameSaving,
        isGroupEditorUngrouping,
        normalizedGroupEditorNameDraft,
        hasGroupEditorNameChanged,
        totalRows,
        pageCount,
        pagedItems,
        isHeaderChecked,
        checkboxData,
        onGroupEditorOpen,
        onGroupEditorClose,
        onGroupEditorModalVisibilityChange,
        renameGroupEditor,
        onGroupEditorRenameSubmit,
        onHeaderCheckboxChange,
        onCheckboxChange,
        onUngroupSelected,
    };
};
